import { forwardRef } from 'preact/compat';
import { useCallback } from 'preact/hooks';

import cx from 'clsx';

import { IInputProps } from '@shared/components/core/input/Input.types';
import { Image } from '@shared/components/core';

import InfoRed from '@assets/icons/infoExclamationRed.svg';

import styles from './Input.module.scss';

const InputBase = forwardRef<HTMLInputElement, IInputProps>(
	(
		{
			type = 'text',
			value = '',
			label = '',
			placeholder = '',
			onChange,
			className,
			prefix,
			suffix,
			onBlur,
			errorMessage,
			showError = false,
			wrapperClassName = '',
			onPaste,
			disabled = false,
			...props
		},
		ref,
	) => {
		const handleBlur = useCallback(
			(event: Event) => {
				if (onBlur) {
					onBlur(event);
				}
			},
			[onBlur],
		);

		const handleChange = useCallback(
			(event: Event) => {
				const target = event.target as HTMLInputElement;
				if (onChange) {
					onChange(target.value, event);
				}
			},
			[onChange],
		);

		const handlePaste = useCallback(
			(event: Event) => onPaste?.(event),
			[onPaste],
		);

		return (
			<div className={wrapperClassName}>
				{label && <div className={cx('my2 lighter h6 lh6 dark3')}>{label}</div>}
				<div className="relative">
					{prefix && (
						<div
							className={cx(
								'absolute d-flex justify-center items-center flex-column left0 bottom0',
								styles.contextContainer,
							)}
						>
							{prefix}
						</div>
					)}
					<input
						ref={ref}
						type={type}
						value={value}
						placeholder={placeholder}
						onInput={handleChange}
						onPaste={handlePaste}
						className={cx(
							'p4 h4 lighter lh6 border-radius2 width100 border1 border-grey2 bg-white dark1',
							styles.inputField,
							{
								[styles.error]: errorMessage,
								[styles.suffixPadding]: !!suffix,
								[styles.prefixPadding]: !!prefix,
							},
							className,
						)}
						onBlur={handleBlur}
						disabled={disabled}
						{...props}
					/>
					{suffix && (
						<div
							className={cx(
								'absolute d-flex justify-center items-center flex-column right0 bottom0',
								styles.contextContainer,
							)}
						>
							{suffix}
						</div>
					)}
				</div>
				<div
					className={cx(
						'h6 mt1 overflow-hidden d-flex justify-start items-start',
						styles.errorMessage,
						{
							[styles.show]: showError && errorMessage,
						},
					)}
					id="input-error-text"
				>
					{errorMessage && (
						<Image src={InfoRed} alt={`${label} error`} className="mr1" />
					)}
					{errorMessage}
				</div>
			</div>
		);
	},
);

export default InputBase;
